import { publicRequest } from "./publicInviteApi.js";
import { getLoadedRuntimeConfig } from "../config/runtimeConfig.js";

/**
 * The employer's two unauthenticated calls, reached by a public resume link.
 *
 * Uses publicInviteApi's request seam rather than the shared apiClient, for
 * exactly the reason that module already documents: apiRequest() attaches an
 * Authorization header whenever a session token happens to exist in the tab.
 * That would be actively wrong here. An employer never has a session, and an
 * OPERATOR very often does - they will open the link themselves to check what
 * they just sent. If the operator's token rode along, the request would stop
 * being "somebody holding this link is answering" and the audit trail would be
 * a lie about who decided.
 *
 * A separate module from the invite-card calls, matching the standing rule that
 * the two sides never share one "public" module.
 */

const RESUME_PATH = "/public/worker-resume";

export function getPublicWorkerResume(token, options) {
  return publicRequest(`${RESUME_PATH}?token=${encodeURIComponent(token)}`, {
    ...options,
    method: "GET",
  });
}

/**
 * รับเข้าทำงาน or ไม่รับ. The token goes in the BODY, not the query string:
 * this is the request that changes something, and a secret in a URL ends up in
 * access logs, proxy logs and browser history.
 */
export function submitEmployerResumeDecision({ token, decision, note }, options) {
  return publicRequest(`${RESUME_PATH}/decision`, {
    ...options,
    method: "POST",
    body: JSON.stringify({ token, decision, note: note ?? null }),
  });
}

/**
 * Builds the URL an operator copies and sends to the employer.
 *
 * DERIVED FROM publicInviteBaseUrl RATHER THAN A NEW CONFIG KEY. That value
 * already names the one public origin this deployment serves worker-facing
 * pages from (".../register"), and every runtime-config.json in every
 * environment already sets it. A new key would be blank in all of them until
 * somebody remembered to fill it in, and the first symptom would be an
 * operator sending an employer a broken link.
 *
 * Falls back to the current origin when no public base is configured at all,
 * which is what a local dev server wants.
 */
export function buildEmployerResumeUrl(token) {
  if (!token) {
    return null;
  }

  const configured = getLoadedRuntimeConfig()?.publicInviteBaseUrl;
  const origin =
    typeof configured === "string" && configured.trim().length > 0
      ? configured.trim().replace(/\/register\/?$/, "").replace(/\/$/, "")
      : typeof window !== "undefined"
        ? window.location.origin
        : "";

  return `${origin}/worker-resume?token=${encodeURIComponent(token)}`;
}
